import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/auth/auth-context';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { CommandPalette } from './command-palette';
import { Sidebar } from './sidebar';
import { Topbar } from './topbar';

const COLLAPSE_KEY = 'tl.nav-collapsed';

/**
 * Read straight from storage rather than through state so the skeleton and the
 * real shell start from the same width - otherwise the sidebar flashes in and
 * then collapses once auth resolves.
 */
function storedCollapsed(): boolean {
  return localStorage.getItem(COLLAPSE_KEY) === '1';
}

export function AppShell() {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [commandOpen, setCommandOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  // Desktop only. Below `lg` the sidebar is already a sheet the topbar opens,
  // so hiding it there would leave no way back to the navigation.
  const [navCollapsed, setNavCollapsed] = useState(storedCollapsed);

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, navCollapsed ? '1' : '0');
  }, [navCollapsed]);

  // `g` followed by a letter jumps between the screens people use hourly.
  // Chorded rather than single-key so it never fires while someone is typing.
  useEffect(() => {
    let pendingG = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;

      // Checked ahead of the typing guard: a modified chord cannot be mistaken
      // for someone filling in a field, and ⌘B is where people reach for this.
      if (event.key.toLowerCase() === 'b' && (event.metaKey || event.ctrlKey) && !event.altKey) {
        event.preventDefault();
        setNavCollapsed((collapsed) => !collapsed);
        return;
      }

      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (target?.isContentEditable || event.metaKey || event.ctrlKey || event.altKey) return;

      if (pendingG) {
        const destination = { o: '/', s: '/sessions', i: '/inbox', c: '/chats' }[event.key];
        pendingG = false;
        if (destination) {
          event.preventDefault();
          navigate(destination);
        }
        return;
      }

      if (event.key === 'g') {
        pendingG = true;
        clearTimeout(timer);
        timer = setTimeout(() => {
          pendingG = false;
        }, 900);
      }
    };

    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      clearTimeout(timer);
    };
  }, [navigate]);

  useEffect(() => setNavOpen(false), [location.pathname]);

  if (isLoading) return <ShellSkeleton />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

  return (
    <div className="flex h-dvh overflow-hidden bg-canvas">
      {/*
        The aside keeps its own width and this wrapper clips it, so collapsing
        slides the panel out instead of reflowing every row inside it. `inert`
        takes the hidden links out of the tab order and off the screen reader.
      */}
      <div
        className={cn(
          'hidden shrink-0 overflow-hidden transition-[width] duration-200 ease-out motion-reduce:transition-none lg:block',
          navCollapsed ? 'w-0' : 'w-sidebar',
        )}
        inert={navCollapsed || undefined}
      >
        <Sidebar />
      </div>

      <Sheet open={navOpen} onOpenChange={setNavOpen}>
        <SheetContent side="left" className="w-sidebar p-0">
          <Sidebar onNavigate={() => setNavOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          onOpenCommand={() => setCommandOpen(true)}
          onOpenNav={() => setNavOpen(true)}
          navCollapsed={navCollapsed}
          onToggleNav={() => setNavCollapsed((collapsed) => !collapsed)}
        />
        <main className="min-h-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
    </div>
  );
}

/** Matches the real layout so the shell does not jump once auth resolves. */
function ShellSkeleton() {
  if (storedCollapsed()) {
    return (
      <div className="flex h-dvh overflow-hidden bg-canvas">
        <div className="flex-1">
          <div className="h-topbar border-b border-line" />
          <div className="space-y-3 p-5">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-40 w-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-dvh overflow-hidden bg-canvas">
      <div className="hidden w-sidebar flex-col gap-2 border-r border-line bg-surface-sunken p-3 lg:flex">
        <Skeleton className="h-5 w-28" />
        <div className="mt-4 space-y-1.5">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="h-6 w-full" />
          ))}
        </div>
      </div>
      <div className="flex-1">
        <div className="h-topbar border-b border-line" />
        <div className="space-y-3 p-5">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    </div>
  );
}
